import type { UserRole } from '@/types';

/**
 * #408: where each Dashboard summary card opens.
 *
 * A card is only a link when its destination can honour the number on the card
 * EXACTLY; otherwise it points at the unfiltered list, or at nothing. Every
 * target is checked against the route's `allowedRoles` in App.tsx so a card
 * never leads to an Unauthorized page.
 */

// Mirrors the `/quotes` route's allowedRoles in App.tsx.
const QUOTES_ROLES: ReadonlySet<UserRole> = new Set(['broker_admin', 'broker_employee', 'posp', 'super_posp']);

/** Total Policies: the Policies list as-is. */
export const TOTAL_POLICIES_TARGET = '/policies';

/**
 * Active Policies: `lifecycle_status=active` is the same predicate the tile's
 * count uses (`Policy.objects.active_by_date()` == `lifecycle_q('active')`).
 */
export const ACTIVE_POLICIES_TARGET = '/policies?lifecycle_status=active';

/**
 * Pending Quotes counts every non-terminal quote request (many statuses), and
 * the Quotes page filters on one status at a time, so the link is unfiltered.
 * Null for roles that cannot open /quotes (e.g. country_admin).
 */
export function pendingQuotesTarget(role: UserRole | undefined): string | null {
  return role && QUOTES_ROLES.has(role) ? '/quotes' : null;
}

/** Super admin platform cards (route is super_admin only, as is the card row). */
export const COUNTRY_ADMINS_TARGET = '/admin/country-admins';
export const COUNTRIES_TARGET = '/admin/countries';

/**
 * Insurer dashboard. Keyed by card label; a missing key means "not a link".
 *
 * - Total Policies -> /policies: the insurer list's default is the same issued,
 *   non-archived book the card counts (#407).
 * - Active Policies -> /policies?lifecycle_status=active (#408). The card counts
 *   with the same lifecycle rule (#641), so card and list agree.
 * - Total Premium (GWP) -> /insurance/reports: the insurer business report
 *   shows the same gross written premium (all time, no date range set).
 * - Claims -> /claims, Active Brokers -> /insurance/channels,
 *   Products Offered -> /insurance/policies (all open to insurer roles).
 * - Commission Paid / Payable -> /insurance/commissions?tab=ledger (#410, #703): the
 *   insurer's earned / paid / payable breakdown, built from the same figures as the cards.
 * - Policy Access Granted has no matching list, so it stays static.
 * - Pending Broker Requests -> the broker requests list filtered to pending
 *   (#408); it lists the same relation rows the card counts.
 */
export const INSURER_CARD_TARGETS: Readonly<Record<string, string>> = {
  'Total Policies': TOTAL_POLICIES_TARGET,
  'Active Policies': ACTIVE_POLICIES_TARGET,
  'Total Premium (GWP)': '/insurance/reports',
  Claims: '/claims',
  'Active Brokers': '/insurance/channels',
  'Products Offered': '/insurance/policies',
  'Commission Paid': '/insurance/commissions?tab=ledger',
  'Commission Payable': '/insurance/commissions?tab=ledger',
  'Pending Broker Requests': '/insurance/broker-requests?status=pending',
};
